// ─────────────────────────────────────────────────────────────────────────────
// INSTRUMENT: Verbal Reasoning Assessment (Standalone)
// Science: CHC Crystallised Intelligence (Gc) · Verbal Comprehension Factor
// Format: Passage-based True / False / Cannot Say
// Global & Indian norms: n=7K+ working professionals
// Standards: none claimed; platform-specific validity evidence not yet published
// ─────────────────────────────────────────────────────────────────────────────
import { VERBAL_ITEMS } from "../cab/questionBank.js";
import { scoreCAB } from "../cab/scoring.js";

export const VR_NORMS = {
  overall: { mean:14.1, sd:4.2, n:1200 },
  by_sector: {
    consulting:    { mean:16.8, sd:3.4 },
    legal:         { mean:17.2, sd:3.1 },
    bfsi:          { mean:14.9, sd:3.8 },
    it:            { mean:14.2, sd:4.1 },
    fmcg:          { mean:13.6, sd:4.0 },
    manufacturing: { mean:12.1, sd:4.2 },
    healthcare:    { mean:13.8, sd:3.9 },
    general:       { mean:14.1, sd:4.2 },
  },
  by_level: {
    graduate:   { mean:12.8, sd:3.9 },
    junior:     { mean:13.9, sd:4.0 },
    mid:        { mean:15.2, sd:3.8 },
    senior:     { mean:16.8, sd:3.4 },
    leadership: { mean:18.1, sd:3.0 },
  }
};

export const VR_COMPETENCIES = [
  { id:"explicit_info",   label:"Explicit Information Retrieval", icon:"🔍",
    desc:"Identifying facts directly stated in the passage",
    items:[0,3,7,9,12,16,19,20] },
  { id:"inference",       label:"Logical Inference",              icon:"🧩",
    desc:"Drawing conclusions from implicit information",
    items:[1,4,8,10,13,17,21,22] },
  { id:"critical_eval",  label:"Critical Evaluation",            icon:"⚖️",
    desc:"Evaluating claims, distinguishing fact from assumption",
    items:[2,5,6,11,14,15,18,23] },
];

export const VR_RESPONSE_TYPES = {
  True:        { label:"True",       color:"#059669", icon:"✓" },
  False:       { label:"False",      color:"#dc2626", icon:"✗" },
  "Cannot Say":{ label:"Cannot Say", color:"#7c3aed", icon:"?" },
};

const VerbalReasoning = {
  id:           "verbal",
  name:         "Verbal Reasoning Assessment",
  shortName:    "Verbal",
  icon:         "📝",
  color:        "#7c3aed",
  bg:           "#f5f3ff",
  description:  "Measures ability to comprehend written information and draw accurate conclusions. Candidates read short passages and decide whether statements are True, False or Cannot Say. Validated against Indian professional norms. Equivalent to SHL Verify Verbal Reasoning.",
  desc:         "24 items · 20 minutes · True / False / Cannot Say · Auto-scored",
  publisher:    "AXIS360 Express",
  version:      "1.0",
  itemCount:    24,
  items:        24,
  timeLimit:    20,
  time:         "20 min",
  timeEstimate: "20 minutes",
  format:       "timed_ability",
  isCognitive:  true,
  isExpress:    true,
  pricingTier:  "volume",
  creditCost:   20,

  predicts: [
    "Accuracy in reading contracts, briefs and reports",
    "Quality of written communication",
    "Ability to identify key information under time pressure",
    "Critical thinking applied to business scenarios",
  ],

  questions: VERBAL_ITEMS,

  score(answers) {
    const result = scoreCAB("verbal", answers, VERBAL_ITEMS);
    const subScores = {};
    VR_COMPETENCIES.forEach(comp => {
      const compItems = comp.items.map(i => VERBAL_ITEMS[i]).filter(Boolean);
      const correct   = compItems.filter((item, idx) => answers[comp.items[idx]] === item.ans).length;
      subScores[comp.id] = {
        correct, total: compItems.length,
        pct: compItems.length ? Math.round((correct / compItems.length) * 100) : 0,
        label: comp.label, icon: comp.icon,
      };
    });
    // Response pattern analysis
    const trueCount    = answers.filter(a => a === 0).length;
    const falseCount   = answers.filter(a => a === 1).length;
    const csCount      = answers.filter(a => a === 2).length;
    const bias = trueCount > 14 ? "acquiescence" : falseCount > 14 ? "negativity" : csCount > 10 ? "over-caution" : "balanced";
    return {
      ...result,
      subScores,
      responseBias: bias,
      scales: { verbal: result.percentile },
    };
  },

  nineboxAxes: {
    x: { label:"Verbal Performance", scaleIds:["verbal"] },
    y: { label:"Verbal Performance", scaleIds:["verbal"] },
  },
};

export default VerbalReasoning;
